/**
 * Vincent #124 (Eric 2026-05-13) — Panneau de diagnostic de connexion.
 *
 * Affiché à la place du message générique "Connexion temporairement
 * indisponible" pour donner à l'utilisateur (et à l'équipe support) un
 * rapport actionnable sur ce qui bloque.
 *
 * Teste séquentiellement :
 *   1. `fetch /api/diagnostics/ping` (anonyme, sans cookie)
 *   2. `fetch /api/health` (anonyme)
 *   3. `fetch /api/diagnostics/whoami` (anonyme — voit l'IP & headers CF)
 *   4. `fetch /api/auth/me` (avec cookies — teste vraiment l'auth)
 *
 * Génère un rapport JSON copiable + l'envoie automatiquement à
 * `/api/client-errors` pour que l'admin puisse l'analyser après coup.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Spinner, CheckCircle, XCircle, Copy, PaperPlaneTilt, ArrowClockwise,
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";

// Tâche 1395 — Les deux langues sur la même ligne (motif 1324b). Le
// `label` français reste celui qu'on écrit dans le RAPPORT envoyé au
// support : ce rapport est lu par l'équipe Knoria, pas par la personne
// bloquée, et le traduire compliquerait le dépannage sans servir personne.
const PROBES = [
  { key: "ping", label: "Ping API (anonyme)", label_en: "API ping (anonymous)", url: "/api/diagnostics/ping", creds: "omit" },
  { key: "health", label: "Santé serveur", label_en: "Server health", url: "/api/health", creds: "omit" },
  { key: "whoami", label: "Identification réseau (CDN, IP, pays)", label_en: "Network identification (CDN, IP, country)", url: "/api/diagnostics/whoami", creds: "omit" },
  { key: "authMe", label: "Session utilisateur (avec cookies)", label_en: "User session (with cookies)", url: "/api/auth/me", creds: "include" },
];

async function runProbe(probe) {
  const url = `${API}${probe.url}`;
  const t0 = performance.now();
  const out = { key: probe.key, label: probe.label, url, ok: false, status: null, ms: null, error: null, body: null, attempts: 0 };
  // Eric / Vincent (2026-05-14, Vincent login incident) — Retry layer.
  //   - Each probe is attempted up to 3 times with exponential backoff (250ms,
  //     750ms). Transient blips (Cloudflare cold-cache, brief DNS hiccup, a
  //     single proxy 502) no longer trigger the alarmist "Aucune connexion"
  //     banner — the diagnostic only marks a probe as failed when ALL 3 tries
  //     fail. This makes the diagnostic much less twitchy in real-world
  //     conditions (mobile networks, VPN, etc.).
  //   - Each attempt also has a hard 8s AbortController timeout so a hung
  //     proxy doesn't freeze the whole panel.
  const MAX_ATTEMPTS = 3;
  const BACKOFF_MS = [0, 250, 750]; // delay BEFORE attempt N
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (BACKOFF_MS[attempt - 1]) {
      await new Promise(r => setTimeout(r, BACKOFF_MS[attempt - 1]));
    }
    out.attempts = attempt;
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 8000);
    try {
      const r = await fetch(url, {
        method: "GET",
        credentials: probe.creds,
        cache: "no-store",
        signal: ctrl.signal,
      });
      clearTimeout(timeoutId);
      out.status = r.status;
      out.ms = Math.round(performance.now() - t0);
      out.ok = r.status >= 200 && r.status < 400;
      try {
        const txt = await r.text();
        out.body = txt.length > 800 ? txt.slice(0, 800) + "…" : txt;
      } catch { /* ignore body parse */ }
      if (out.ok) return out; // success — stop retrying
      // 4xx (other than 401 on authMe which is a valid state) → no point retrying
      if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429) {
        return out;
      }
    } catch (e) {
      clearTimeout(timeoutId);
      out.ms = Math.round(performance.now() - t0);
      out.error = String(e?.name === "AbortError" ? "timeout(8s)" : (e?.message || e));
      if (/Failed to fetch|NetworkError|TypeError/.test(out.error)) {
        out.classification = "network_or_cors_or_extension_block";
      }
    }
  }
  return out;
}

function summarize(probes) {
  const ping = probes.find((p) => p.key === "ping");
  const health = probes.find((p) => p.key === "health");
  const whoami = probes.find((p) => p.key === "whoami");
  const authMe = probes.find((p) => p.key === "authMe");
  const pingOk = ping?.ok;
  const healthOk = health?.ok;
  const whoamiOk = whoami?.ok;
  const authOk = authMe?.ok;
  const authStatus = authMe?.status;

  // Eric 2026-07-16 (SPEC STRICK — Vincent 520 fausse alerte) —
  // Distinguer les VRAIES erreurs réseau (uBlock/VPN → fetch throws) des
  // erreurs SERVEUR (Cloudflare 520/521/522/523/524/525/502/503/504 →
  // fetch réussit mais renvoie un status 5xx). Le message « uBlock/VPN »
  // faisait perdre du temps à Vincent quand la vraie cause était un
  // pod K8s en train de redémarrer.
  const CF_ORIGIN_DOWN_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525]);
  const isServerDown = (p) =>
    p?.status != null && (CF_ORIGIN_DOWN_STATUSES.has(p.status) || p.status >= 500);
  const isTrueNetworkFailure = (p) =>
    !p?.ok && p?.status == null && p?.error;

  // Cas 1 : serveur backend down (5xx reçu au moins une fois)
  if (isServerDown(ping) || isServerDown(health)) {
    const cfStatus = ping?.status || health?.status || 520;
    return {
      severity: "server_down",
      title: "Serveur temporairement indisponible",
      title_en: "Server temporarily unavailable",
      cause: `Le serveur Knoria répond avec une erreur ${cfStatus} (redémarrage en cours ou déploiement récent). Ta connexion internet est OK. Aucune action requise de ta part.`,
      cause_en: `The Knoria server is answering with a ${cfStatus} error (restart in progress or recent deployment). Your internet connection is fine. Nothing to do on your side.`,
      actions: [
        "Réessaie dans 30 à 60 secondes (redémarrage automatique en cours)",
        "Si le problème persiste après 5 minutes, préviens l'équipe technique",
        "Aucune extension ni VPN à désactiver — le blocage est côté serveur",
      ],
      actions_en: [
        "Try again in 30 to 60 seconds (automatic restart in progress)",
        "If it lasts more than 5 minutes, tell the technical team",
        "No extension or VPN to disable — the blocking is on the server side",
      ],
      auto_retry_ms: 30000,
    };
  }

  // Cas 2 : vraie erreur réseau (fetch a throw, pas de status HTTP) → uBlock/VPN/pare-feu
  if (isTrueNetworkFailure(ping) && isTrueNetworkFailure(health)) {
    return {
      severity: "critical",
      title: "Aucune connexion à Knoria",
      title_en: "No connection to Knoria",
      cause: "Votre navigateur n'arrive pas à atteindre nos serveurs. Causes possibles : extension de blocage de pubs (uBlock, Privacy Badger), pare-feu d'entreprise, ou VPN.",
      cause_en: "Your browser cannot reach our servers. Likely causes: an ad-blocking extension (uBlock, Privacy Badger), a corporate firewall, or a VPN.",
      actions: [
        "Essayez en navigation privée (Ctrl+Shift+N)",
        "Désactivez temporairement les extensions du navigateur",
        "Vérifiez votre connexion (essayez un autre réseau, p.ex. 4G mobile)",
        "Essayez un autre navigateur (Chrome ↔ Firefox)",
      ],
      actions_en: [
        "Try a private window (Ctrl+Shift+N)",
        "Temporarily disable your browser extensions",
        "Check your connection (try another network, e.g. mobile data)",
        "Try another browser (Chrome ↔ Firefox)",
      ],
    };
  }

  // Cas 3 : ping ET health échouent sans classification claire (ancien fallback)
  if (!pingOk && !healthOk) {
    return {
      severity: "critical",
      title: "Aucune connexion à Knoria",
      title_en: "No connection to Knoria",
      cause: "Votre navigateur n'arrive pas à atteindre nos serveurs. Causes possibles : extension de blocage de pubs (uBlock, Privacy Badger), pare-feu d'entreprise, ou VPN.",
      cause_en: "Your browser cannot reach our servers. Likely causes: an ad-blocking extension (uBlock, Privacy Badger), a corporate firewall, or a VPN.",
      actions: [
        "Essayez en navigation privée (Ctrl+Shift+N)",
        "Désactivez temporairement les extensions du navigateur",
        "Vérifiez votre connexion (essayez un autre réseau, p.ex. 4G mobile)",
        "Essayez un autre navigateur (Chrome ↔ Firefox)",
      ],
      actions_en: [
        "Try a private window (Ctrl+Shift+N)",
        "Temporarily disable your browser extensions",
        "Check your connection (try another network, e.g. mobile data)",
        "Try another browser (Chrome ↔ Firefox)",
      ],
    };
  }
  if (pingOk && !authOk && authStatus === 401) {
    return {
      severity: "warning",
      title: "Session expirée",
      title_en: "Session expired",
      cause: "Le serveur est joignable mais votre session a expiré.",
      cause_en: "The server is reachable but your session has expired.",
      actions: ["Reconnectez-vous depuis la page de connexion."],
      actions_en: ["Sign in again from the login page."],
    };
  }
  if (pingOk && !authOk && authStatus === 0) {
    return {
      severity: "warning",
      title: "Cookies bloqués",
      title_en: "Cookies blocked",
      cause: "Le ping anonyme passe mais l'appel authentifié échoue — vos cookies sont peut-être bloqués par le navigateur.",
      cause_en: "The anonymous ping works but the authenticated call fails — your browser may be blocking cookies.",
      actions: [
        "Activez les cookies pour knoria.com",
        "Désactivez le mode 'cookies tiers bloqués' temporairement",
      ],
      actions_en: [
        "Allow cookies for knoria.com",
        "Temporarily turn off “block third-party cookies”",
      ],
    };
  }
  if (pingOk && !whoamiOk) {
    return {
      severity: "warning",
      title: "Connectivité partielle",
      title_en: "Partial connectivity",
      cause: "Certains endpoints répondent, d'autres non. Probablement un proxy/CDN qui filtre.",
      cause_en: "Some endpoints answer, others do not. Most likely a proxy or CDN filtering traffic.",
      actions: ["Réessayez dans 30 secondes", "Désactivez le VPN si actif"],
      actions_en: ["Try again in 30 seconds", "Turn off the VPN if one is active"],
    };
  }
  if (pingOk && healthOk && authOk) {
    return {
      severity: "success",
      title: "Tout fonctionne",
      title_en: "Everything works",
      cause: "La connectivité est rétablie. Vous pouvez réessayer.",
      cause_en: "Connectivity is back. You can try again.",
      actions: ["Cliquez sur 'Recharger la page'"],
      actions_en: ["Click “Reload the page”"],
    };
  }
  return {
    severity: "unknown",
    title: "Diagnostic inconclusif",
    title_en: "Inconclusive diagnosis",
    cause: "Le rapport ci-dessous contient les détails techniques.",
    cause_en: "The report below holds the technical details.",
    actions: ["Copiez le rapport et envoyez-le à support@knoria.com"],
    actions_en: ["Copy the report and send it to support@knoria.com"],
  };
}

export default function ConnectionDiagnostic({ userEmail }) {
  // Tâche 1395 — L'ÉCRAN suit la langue ; le RAPPORT envoyé au support reste
  // français (il est lu par l'équipe Knoria, pas par la personne bloquée).
  const { lang, tr } = useLang();
  const L = (o, cle) => (lang === "en" && o?.[`${cle}_en`] ? o[`${cle}_en`] : o?.[cle]);
  const [running, setRunning] = useState(false);
  const [probes, setProbes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [reportSent, setReportSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const autoSentRef = useRef(false);

  const run = useCallback(async () => {
    setRunning(true);
    setReportSent(false);
    const results = [];
    for (const p of PROBES) {
      results.push({ key: p.key, label: p.label, label_en: p.label_en, running: true });
      setProbes([...results]);
      let r = await runProbe(p);
      // Eric / Vincent (2026-05-14, Vincent login incident) — Cross-origin
      // fallback. If the configured API base failed AND we're on a custom
      // domain (origin != API host), try the SAME-ORIGIN path before declaring
      // failure. This rescues users whose browser/network blocks the secondary
      // host (Safari ITP, Brave Shields, corporate firewall, Cloudflare bot
      // challenge on emergent.host but not on knoria.com).
      if (!r.ok && typeof window !== "undefined" && window.location) {
        const origin = window.location.origin || "";
        if (API && origin && API !== origin) {
          const fallbackUrl = `${origin}${p.url}`;
          const t0 = performance.now();
          const ctrl = new AbortController();
          const tmo = setTimeout(() => ctrl.abort(), 8000);
          try {
            const resp = await fetch(fallbackUrl, {
              method: "GET",
              credentials: p.creds,
              cache: "no-store",
              signal: ctrl.signal,
            });
            clearTimeout(tmo);
            if (resp.status >= 200 && resp.status < 400) {
              const ms = Math.round(performance.now() - t0);
              let body = null;
              try { const t = await resp.text(); body = t.length > 800 ? t.slice(0, 800) + "…" : t; } catch { /* ignore */ }
              r = {
                ...r,
                ok: true,
                status: resp.status,
                ms,
                body,
                url: fallbackUrl,
                fallback_used: true,
                primary_failed_url: `${API}${p.url}`,
                // i18n-donnee: trace technique rangée dans le rapport envoyé au support.
                note: "Bascule en same-origin réussie après échec du domaine principal.",
              };
            }
          } catch (e) {
            clearTimeout(tmo);
            r.fallback_error = String(e?.message || e);
          }
        }
      }
      results[results.length - 1] = r;
      setProbes([...results]);
    }
    setSummary(summarize(results));
    setRunning(false);
  }, []);

  // Auto-run on mount.
  useEffect(() => { run(); }, [run]);

  // Auto-send report to backend once (best-effort) so admins see it.
  useEffect(() => {
    if (running || !summary || autoSentRef.current) return;
    if (probes.length === 0) return;
    autoSentRef.current = true;
    const payload = {
      kind: "auth_unreachable",
      user_email: userEmail || "",
      message: summary.title,
      url: typeof window !== "undefined" ? window.location.href : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      ts: new Date().toISOString(),
      attempts: probes,
      nav: typeof navigator !== "undefined" ? {
        onLine: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
        language: navigator.language,
        platform: navigator.platform,
      } : {},
      summary,
    };
    // Eric / Vincent (2026-05-14, Defense Layer 3) — also ping the self-audit
    // endpoint so admins get a structured verdict (cross_origin_mismatch /
    // missing_config / config_ok) rather than just raw probe results.
    const origin = typeof window !== "undefined" && window.location ? window.location.origin : "";
    const auditBody = JSON.stringify({
      configured_api_base: API || "",
      page_origin: origin,
      user_email: userEmail || "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    });
    // Try the configured API first; if it fails AND origin differs, fall back same-origin.
    const tryAudit = async () => {
      const headers = { "Content-Type": "application/json" };
      try {
        await fetch(`${API}/api/diagnostics/self-audit`, {
          method: "POST", headers, credentials: "omit", body: auditBody, cache: "no-store",
        });
      } catch {
        if (origin && API !== origin) {
          try {
            await fetch(`${origin}/api/diagnostics/self-audit`, {
              method: "POST", headers, credentials: "omit", body: auditBody, cache: "no-store",
            });
          } catch { /* swallow */ }
        }
      }
    };
    tryAudit();
    fetch(`${API}/api/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(payload),
      cache: "no-store",
    }).then(() => setReportSent(true)).catch(() => { /* swallow */ });
  }, [running, summary, probes, userEmail]);

  const reportText = (() => {
    const lines = [
      // i18n-donnee: rapport destiné à l'équipe Knoria, toujours en français.
      `KNORIA — Diagnostic de connexion`,
      `Date : ${new Date().toISOString()}`,
      `Utilisateur : ${userEmail || "(non connecté)"}`,
      `URL : ${typeof window !== "undefined" ? window.location.href : ""}`,
      `Navigateur : ${typeof navigator !== "undefined" ? navigator.userAgent : ""}`,
      `Cookies activés : ${typeof navigator !== "undefined" ? navigator.cookieEnabled : "?"}`,
      `Mode en ligne : ${typeof navigator !== "undefined" ? navigator.onLine : "?"}`,
      ``,
      `=== Résumé ===`,
      `Sévérité : ${summary?.severity || "—"}`,
      `Cause probable : ${summary?.cause || "—"}`,
      ``,
      `=== Tests ===`,
    ];
    probes.forEach((p) => {
      lines.push(
        `- ${p.label} (${p.url || ""})`,
        `  status=${p.status ?? "—"} ms=${p.ms ?? "—"} ok=${!!p.ok} error=${p.error || "—"}`,
      );
    });
    return lines.join("\n");
  })();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard blocked */ }
  };

  const sevColor = {
    critical: "bg-red-50 border-red-300 text-red-900",
    server_down: "bg-blue-50 border-blue-300 text-blue-900",
    warning: "bg-amber-50 border-amber-300 text-amber-900",
    success: "bg-teal-50 border-teal-300 text-teal-900",
    unknown: "bg-zinc-50 border-zinc-300 text-zinc-900",
  }[summary?.severity] || "bg-zinc-50 border-zinc-300 text-zinc-900";

  // Eric 2026-07-16 (SPEC STRICK — Vincent 520) — Auto-retry si le serveur
  // est down (Cloudflare 5xx). Toutes les 30s, on relance discrètement les
  // probes. Si tout revient, le summary.severity passe à "success" et l'user
  // voit immédiatement « Tout fonctionne, cliquez sur Recharger la page ».
  const [countdown, setCountdown] = useState(0);
  useEffect(() => {
    if (running || summary?.severity !== "server_down") {
      setCountdown(0);
      return;
    }
    const retryMs = summary?.auto_retry_ms || 30000;
    setCountdown(Math.round(retryMs / 1000));
    const timer = setInterval(() => {
      setCountdown((c) => (c > 1 ? c - 1 : 0));
    }, 1000);
    const retryTimeout = setTimeout(() => {
      clearInterval(timer);
      run();
    }, retryMs);
    return () => {
      clearInterval(timer);
      clearTimeout(retryTimeout);
    };
  }, [summary, running, run]);

  return (
    <div className="text-left max-w-xl mx-auto" data-testid="connection-diagnostic">
      {summary && (
        <div className={`mb-4 px-4 py-3 rounded-lg border ${sevColor}`} data-testid="diag-summary">
          <div className="text-sm font-semibold mb-1">{L(summary, "title")}</div>
          <div className="text-xs mb-2 opacity-90">{L(summary, "cause")}</div>
          <ul className="text-xs space-y-1 list-disc ml-4">
            {(L(summary, "actions") || []).map((a, i) => <li key={i}>{a}</li>)}
          </ul>
          {summary.severity === "server_down" && countdown > 0 && (
            <div className="mt-3 pt-2 border-t border-blue-200 flex items-center gap-2 text-xs" data-testid="diag-auto-retry">
              <Spinner size={12} className="animate-spin" />
              <span>{tr("Nouvel essai automatique dans", "Automatic retry in")} <strong>{countdown}s</strong>…</span>
            </div>
          )}
        </div>
      )}

      <div className="space-y-1.5 mb-4">
        {probes.map((p) => (
          <div key={p.key} className="flex items-center gap-2 text-xs bg-white border border-zinc-200 rounded px-3 py-2" data-testid={`diag-probe-${p.key}`}>
            {p.running ? (
              <Spinner size={14} className="animate-spin text-zinc-500 flex-shrink-0" />
            ) : p.ok ? (
              <CheckCircle size={14} weight="fill" className="text-teal-600 flex-shrink-0" />
            ) : (
              <XCircle size={14} weight="fill" className="text-red-600 flex-shrink-0" />
            )}
            <span className="flex-1 truncate">{L(p, "label")}</span>
            <span className="text-[10px] text-zinc-500 font-mono">
              {p.status != null ? `HTTP ${p.status}` : (p.error ? tr("réseau", "network") : "…")}
              {p.ms != null ? ` · ${p.ms}ms` : ""}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        <button
          onClick={run}
          disabled={running}
          className="px-3 py-1.5 bg-[#002FA7] text-white rounded-md text-xs font-medium hover:bg-[#001f7a] disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="diag-rerun-btn"
        >
          <ArrowClockwise size={12} weight="bold" />
          {running ? tr("En cours…", "Running…") : tr("Relancer le diagnostic", "Run the diagnosis again")}
        </button>
        <button
          onClick={handleCopy}
          disabled={running || probes.length === 0}
          className="px-3 py-1.5 bg-white text-zinc-700 border border-zinc-300 rounded-md text-xs font-medium hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="diag-copy-btn"
        >
          <Copy size={12} weight="bold" />
          {copied ? tr("Copié ✓", "Copied ✓") : tr("Copier le rapport", "Copy the report")}
        </button>
        <a
          href={`mailto:support@knoria.com?subject=${encodeURIComponent("Knoria — Diagnostic de connexion")}&body=${encodeURIComponent(reportText)}`}
          className="px-3 py-1.5 bg-white text-zinc-700 border border-zinc-300 rounded-md text-xs font-medium hover:bg-zinc-50 inline-flex items-center gap-1.5"
          data-testid="diag-mail-btn"
        >
          <PaperPlaneTilt size={12} weight="bold" />
          {tr("Envoyer au support", "Send to support")}
        </a>
      </div>
      {reportSent && (
        <p className="text-[10px] text-teal-600 text-center mt-2" data-testid="diag-auto-sent">
          {tr("✓ Rapport automatiquement transmis à l\u2019équipe Knoria", "✓ Report automatically sent to the Knoria team")}
        </p>
      )}
    </div>
  );
}
